/**
 * Leituras cacheadas — UM lugar para todo request que o front repetia sem
 * o dado ter mudado (New Relic 04-09: /tags/list 56 rpm, /companySettingOne
 * 39 rpm, /companies/listPlan 26 rpm — juntos ~20% do tempo de transação,
 * refeitos a cada ticket aberto).
 *
 * Cada domínio declara: como ler (com chave e TTL) e como invalidar.
 * Quem grava chama o `invalidate` do domínio; quem só lê usa o método de
 * leitura. Mudança feita por OUTRO usuário chega pelo socket — ver
 * `bindCacheInvalidation`, ligado uma vez no useAuth.
 *
 * Motor: services/requestCache.js (single-flight, falha não vira cache, TTL).
 *
 * Rota e evento de invalidação vêm de @markedesk/core-contracts — o backend
 * importa o mesmo contrato, então a string não existe solta nos dois lados.
 */
import api from "./api";
import { requestCache, chaveDeParams } from "./requestCache";
import {
  queueContract,
  tagsContract,
  companySettingOneContract,
  planCompanyContract,
  contactsContract,
  whatsappContract,
  frontendCacheContract,
  auditLogsContract,
  AUDIT_LOG_IMMUTABLE_TTL
} from "@markedesk/core-contracts";

/**
 * O TTL aqui é REDE DE SEGURANÇA, não o mecanismo de correção.
 *
 * Quem mantém o dado certo é a invalidação: o backend emite quando alguém
 * grava, e o socket caindo derruba tudo (ver bindCacheInvalidation). Num cache
 * sem invalidação, TTL curto é o que limita o dado velho — aqui não é. Curto
 * só garante pagar rede por dado que não mudou. Foi o que a medição mostrou:
 * `tags` e `contacts` venciam sem uma única invalidação no meio.
 *
 * Mas infinito também não: a garantia depende de gente lembrar de emitir em
 * código novo, e há prova direta de que esquecem — uma auditoria em 08/09/2026
 * achou 12 caminhos de escrita sem aviso (plano com o emit comentado, tag do
 * SyncTagsService, contato de importação e de campanha...). O 13º vai aparecer.
 *
 * Daí a régua de cada número abaixo: NÃO é "com que frequência muda", é
 * QUANTO DÓI se alguém esquecer de avisar. Por isso `whatsapps` é o mais curto
 * apesar de `contacts` mudar mais — errar ali faz alguém disparar campanha por
 * uma conexão morta.
 */
/**
 * O TTL de cada domínio agora mora NO CONTRATO (`contrato.cache.ttl`), junto
 * do evento que o torna seguro — antes o número ficava aqui e o evento lá,
 * e a relação entre os dois só existia na cabeça de quem escreveu.
 */
/**
 * Lê pelo cache DESTE contrato.
 *
 * O TTL vem junto do contrato em vez de ser passado a cada chamada: ele é
 * decisão do domínio (`contrato.cache.ttl`), não de quem chama. Antes era um
 * terceiro argumento repetido em toda leitura, o que abria espaço para dois
 * pontos do mesmo domínio usarem TTLs diferentes sem ninguém notar.
 */
const cached = (contrato, chave, buscar) =>
  requestCache.cached(chave, buscar, contrato.cache.ttl);

const PREFIX = {
  plan: "plan:",
  companySettings: "companySettings:",
  tags: "tags:",
  queues: "queues:",
  contacts: "contacts:",
  whatsapps: "whatsapps:",
  auditLogs: "auditLogs:"
};

const get = async (url, params) => {
  const { data } = await api.request({ url, method: "GET", params });
  return data;
};

export const cachedApi = {
  /** GET /companies/listPlan/:id — plano da empresa (MessageInput, QueueModal, Checkout). */
  plans: {
    company: (id, params) =>
      cached(planCompanyContract, `${PREFIX.plan}${id}:${chaveDeParams(params)}`, () =>
        get(planCompanyContract.buildRoute(id), params)
      ),
    /** Sem id: limpa todas (plano editado no PlansManager). Com id: só aquela empresa. */
    invalidate: (companyId, motivo) =>
      requestCache.invalidate(companyId ? `${PREFIX.plan}${companyId}:` : PREFIX.plan, motivo)
  },

  /** /companySettings — configurações da empresa (uma coluna ou todas). */
  companySettings: {
    one: (params) =>
      cached(
        companySettingOneContract,
        `${PREFIX.companySettings}one:${chaveDeParams(params)}`,
        () => get(companySettingOneContract.route, params)
      ),
    all: (companyId) =>
      cached(companySettingOneContract, `${PREFIX.companySettings}all:${companyId}`, () =>
        get(`/companySettings/${companyId}`)
      ),
    invalidate: (motivo) => requestCache.invalidate(PREFIX.companySettings, motivo)
  },

  /** GET /tags/list — lista de tags (TagsContainer, TagsFilter, Kanban). */
  tags: {
    list: (params = {}) =>
      cached(tagsContract, `${PREFIX.tags}${chaveDeParams(params)}`, () =>
        get(tagsContract.route, params)
      ),
    invalidate: (motivo) => requestCache.invalidate(PREFIX.tags, motivo)
  },

  /** GET /queue — lista de filas (TransferTicketModalCustom, ScheduleModal, CampaignModal, ChatBots). */
  queues: {
    findAll: () =>
      cached(queueContract, `${PREFIX.queues}list`, () => get(queueContract.route)),
    invalidate: (motivo) => requestCache.invalidate(PREFIX.queues, motivo)
  },

  /**
   * GET /contacts — lista paginada e pesquisável.
   *
   * Diferente dos outros quatro: a chave precisa carregar TODOS os parâmetros,
   * senão uma busca por "joão" devolveria o resultado guardado de "maria", ou
   * a página 2 viria com o conteúdo da 1. É o risco que um cache introduz numa
   * rota com filtro, e o que o teste de chaves distintas trava.
   */
  contacts: {
    list: (params = {}) =>
      cached(contactsContract, `${PREFIX.contacts}${chaveDeParams(params)}`, () =>
        get(contactsContract.route, params)
      ),
    invalidate: (motivo) => requestCache.invalidate(PREFIX.contacts, motivo)
  },

  /**
   * GET /whatsapp — lista de conexões.
   *
   * Quatro telas buscam esta lista ao montar (modal de campanha em três
   * lugares, modal de mensagem ativa do flow builder), então abrir o modal
   * refazia a lista inteira toda vez.
   *
   * É a leitura mais delicada do conjunto: traz `status` e `qrcode`. Mostrar
   * como conectada uma conexão que caiu é pior que mostrar lista velha —
   * alguém escolhe essa conexão para disparar campanha. Por isso invalida
   * pelos DOIS eventos e tem o TTL mais curto.
   */
  whatsapps: {
    list: (params = {}) =>
      cached(whatsappContract, `${PREFIX.whatsapps}${chaveDeParams(params)}`, () =>
        get(whatsappContract.route, params)
      ),
    invalidate: (motivo) => requestCache.invalidate(PREFIX.whatsapps, motivo)
  },

  /**
   * GET /audit-logs — tela de auditoria, paginada por cursor.
   *
   * Sem evento de socket (ver o contrato). Resposta `immutable` (dias fechados)
   * fica guardada por AUDIT_LOG_IMMUTABLE_TTL; o resto pelo TTL curto do
   * contrato, e o botão "Atualizar" derruba tudo.
   */
  auditLogs: {
    list: (companyId, params = {}) =>
      requestCache.cached(
        auditLogsCacheKey(companyId, params),
        () => get(auditLogsContract.route, params),
        page => (page && page.immutable ? AUDIT_LOG_IMMUTABLE_TTL : auditLogsContract.cache.ttl)
      ),
    invalidate: (motivo) => requestCache.invalidate(PREFIX.auditLogs, motivo)
  }
};

/**
 * ÚNICA forma de montar a chave de cache da auditoria.
 *
 * Exige a empresa LOGADA: o logout não limpa o requestCache, e sem ela na
 * chave quem entrasse com outra empresa na mesma aba veria a auditoria da
 * anterior. O `companyId` dos params (super-admin olhando outra empresa) entra
 * junto, pelo chaveDeParams.
 */
export const auditLogsCacheKey = (companyId, params = {}) => {
  if (!Number.isInteger(companyId) || companyId <= 0) {
    throw new Error(`auditLogsCacheKey: empresa logada inválida (${companyId})`);
  }
  return `${PREFIX.auditLogs}${companyId}:${chaveDeParams(params)}`;
};

/**
 * Eventos do backend que significam "esse dado mudou" — emitidos nas escritas
 * pelo TagController, CompanySettingsController e CompanyController. Assinar
 * aqui é o que faz a mudança de OUTRO usuário (ou do super admin) aparecer
 * antes do TTL. Retorna a lista de eventos assinados, para o chamador
 * desligar no logout.
 */
export const cacheInvalidationEvents = (companyId) => {
  // O motivo viaja junto: e o que deixa o devtools dizer QUAL evento derrubou
  // QUAL cache, em vez de so mostrar que algo foi invalidado.
  // Quais FATOS derrubam cada cache vem do próprio contrato
  // (`cache.invalidatedBy`, uma lista de nomes de `events`). O backend emite o
  // fato sem saber que existe cache; é aqui que se decide o que fazer com ele.
  const porEvento = (contrato, aoInvalidar) =>
    contrato.cache.invalidatedBy.map(fato => {
      const evento = contrato.events[fato];
      return [evento, () => aoInvalidar(`socket:${evento}`)];
    });

  return Object.fromEntries([
    porEvento(tagsContract, motivo => cachedApi.tags.invalidate(motivo)),
    porEvento(companySettingOneContract, motivo => cachedApi.companySettings.invalidate(motivo)),
    porEvento(planCompanyContract, motivo => cachedApi.plans.invalidate(companyId, motivo)),
    porEvento(queueContract, motivo => cachedApi.queues.invalidate(motivo)),
    porEvento(contactsContract, motivo => cachedApi.contacts.invalidate(motivo)),
    porEvento(whatsappContract, motivo => cachedApi.whatsapps.invalidate(motivo))
  ].flat());
};

/**
 * Derruba TODOS os caches de leitura.
 *
 * Existe por causa de um furo na garantia de invalidação: ela depende de o
 * evento do backend CHEGAR. Com o socket caído, as escritas acontecem e os
 * eventos se perdem — o cache continuaria servindo dado velho até o TTL
 * (5 minutos, no caso do plano). Ao reconectar não dá para saber o que se
 * perdeu, então a resposta honesta é jogar tudo fora e buscar de novo.
 */
export const invalidateAll = (motivo) => {
  cachedApi.tags.invalidate(motivo);
  cachedApi.companySettings.invalidate(motivo);
  cachedApi.plans.invalidate(undefined, motivo);
  cachedApi.queues.invalidate(motivo);
  cachedApi.contacts.invalidate(motivo);
  cachedApi.whatsapps.invalidate(motivo);
};

/**
 * Pergunta ao backend se o cache deve ficar ligado.
 *
 * Vai pelo `api` CRU, não pelo `cached`: guardar o interruptor pelo mecanismo
 * que ele controla é a armadilha óbvia — ligado, ele ficaria preso ao próprio
 * TTL; desligado, a consulta deixaria de ser cacheada e o comportamento
 * mudaria conforme o próprio valor que está sendo lido.
 *
 * Falha não desliga nada: sem resposta, segue como estava (ligado por padrão).
 * Desligar é a exceção e precisa ser uma ordem explícita, não a consequência
 * de uma rota fora do ar.
 */
export const aplicarConfigDeCache = async () => {
  try {
    const { data } = await api.request({ url: frontendCacheContract.route, method: "GET" });
    requestCache.definirLigado(data?.enabled !== false);
  } catch {
    // Silêncio de propósito: o app não pode deixar de abrir por causa disto.
  }
};

export const bindCacheInvalidation = (socket, companyId) => {
  const events = cacheInvalidationEvents(companyId);
  Object.entries(events).forEach(([event, handler]) => socket.on(event, handler));

  // Nos DOIS momentos, porque cada um cobre uma janela diferente:
  //
  //  - ao CAIR: o que está guardado foi validado por um canal que não existe
  //    mais. A partir daqui, qualquer escrita acontece sem aviso.
  //  - ao VOLTAR: descarta o que tenha sido cacheado durante o apagão — essas
  //    leituras foram feitas justamente enquanto os eventos não chegavam.
  //
  // Sem o segundo, uma tela aberta durante a queda repovoaria o cache com dado
  // que envelheceu no escuro e ele sobreviveria à reconexão.
  const aoCair = () => invalidateAll("socket:caiu");
  const aoVoltar = () => invalidateAll("socket:reconectou");
  socket.on("disconnect", aoCair);
  socket.on("connect", aoVoltar);

  return [...Object.keys(events), "disconnect", "connect"];
};
